import React, { useState, useMemo, useContext, useCallback } from 'react';
import { DOCUMENT_MESSAGES_QUERY } from '../queries';
import { useQueryWithSimpleErrorHandling, useOverRouterContextUtils, useLocalSubscription, useAuthorization } from 'hooks';
import DocumentMessagesComponent from './DocumentMessagesComponent';
import PropTypes from 'prop-types';
import { REFRESH_DOCUMENT_MESSAGES } from '../subscriptions';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const DocumentMessagesContainer = ({ documentId, contextRef }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToViewWarningArea = useAuthorization(AuthorizationCodes.hasRightToViewWarningArea, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const client = useApolloClient();
  const { setProperties } = useOverRouterContextUtils(contextRef);

  const [documentMessageActiveTab, setDocumentMessagesActiveTab] = useState(
    contextRef?.current?.properties?.documentMessageActiveTab ?? false
  );

  const {
    loading: documentMessagesLoading,
    data: documentMessagesData,
    refetch
  } = useQueryWithSimpleErrorHandling(DOCUMENT_MESSAGES_QUERY, {
    variables: { documentId: documentId },
    skip: !documentId
  });

  useLocalSubscription(REFRESH_DOCUMENT_MESSAGES, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data }) => {
      clearDocumentMessagesCache(data.refreshDocumentMessages.documentIds);
      refetch({ documentId });
    }
  });
  const clearDocumentMessagesCache = documentIds => {
    if (documentIds === null || documentIds.length === 0) return;

    documentIds.forEach(documentId => {
      const { cache } = client;
      const cacheKey = Object.keys(cache.data.data).find(x => x.match(`InsuranceDocumentMessage:{"documentId":${documentId}}`));
      if (cacheKey) {
        const normalizedId = cache.identify({ documentId, __typename: 'InsuranceDocumentMessage' });
        cache.evict({ id: normalizedId });
        cache.gc();
      }
    });
  };

  const handleChange = useCallback(
    newValue => {
      if (documentMessageActiveTab === newValue) {
        setDocumentMessagesActiveTab(false);
        setProperties({ documentMessageActiveTab: false });
      } else {
        setDocumentMessagesActiveTab(newValue);
        setProperties({ documentMessageActiveTab: newValue });
      }
    },
    [documentMessageActiveTab, setProperties]
  );

  const handleDocumentMessage = useCallback(
    messageType => {
      switch (messageType) {
        case 'Warning':
          return t('General.DocumentMessages.Warnings').toLowerCase();
        case 'Error':
          return t('General.DocumentMessages.Errors').toLowerCase();
        case 'Info':
          return t('General.DocumentMessages.Info').toLowerCase();
        default:
          return;
      }
    },
    [t]
  );

  const displaySummary = useCallback(
    messageType => {
      let errorMessage = '';

      switch (messageType) {
        case 0:
        case 'Info':
          return (errorMessage = t('General.DocumentMessages.Info'));
        case 'Warning':
          return (errorMessage = t('General.DocumentMessages.Warnings'));
        case 'Error':
          return (errorMessage = t('General.DocumentMessages.Errors'));
        default:
          return errorMessage;
      }
    },
    [t]
  );

  const messageTypeList = useMemo(() => {
    return ['Info', 'Warning', 'Error'];
  }, []);

  return (
    <>
      {!documentMessagesLoading &&
        documentMessagesData &&
        documentMessagesData.documentMessages?.documentMessages?.length > 0 &&
        hasRightToViewWarningArea && (
          <DocumentMessagesComponent
            documentMessages={documentMessagesData.documentMessages?.documentMessages}
            onChange={handleChange}
            getDocumentMessage={handleDocumentMessage}
            displaySummary={displaySummary}
            documentMessageActiveTab={documentMessageActiveTab}
            messageTypeList={messageTypeList}
          />
        )}
    </>
  );
};

DocumentMessagesContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default DocumentMessagesContainer;
